// =============================================================================
// 島の地形高さ場（上陸・接地用）。捕捉済みシーンの島メッシュを無改変で読み、
// CPUで heightfield(最上面y) を構築する。シーンには一切手を加えない。
// =============================================================================
import { findIsland } from './world';
import type { Heightfield } from './types';

const GRID = 320; // heightfield 解像度（島bbox ~1100m → セル ~3.5m）。7mセルでは水面上の小岩を拾えず、泳いで岩の中に入れた

// 島メッシュ群を world 三角形に展開し、xz グリッドへ最大yをラスタライズ
export function buildIslandHeightfield(scene: any): Heightfield | null {
  const island = findIsland(scene);
  if (!island) return null;

  // 椰子は地形(歩行面)に含めない: canopyで地面が浮いて木に登れてしまう問題を防ぎ、
  // 地表は実際の砂/岩面になる → 椰子は登れず、根本(地表)に立って採取できる。
  const PALM_MATS = /Cat_Palm|Areca_Palm|Tropical_Palm|Palme|Root|Blaetter/;
  const meshes: any[] = [];
  island.updateWorldMatrix(true, true);
  island.traverse((o: any) => {
    if (!(o.isMesh && o.geometry && o.geometry.attributes && o.geometry.attributes.position)) return;
    const mat = Array.isArray(o.material) ? (o.material[0] && o.material[0].name) : (o.material && o.material.name);
    if (mat && PALM_MATS.test(mat)) return;
    meshes.push(o);
  });
  // 島グループ外の静的な難破物(スポーン砂州沖の座礁ボートsw02=scene直下・頂点world座標)も
  // 歩行/潜水の当たり判定に含める。浮いている修理対象の難破船は専用OBB(main.ts)で扱い、
  // いかだ/手持ち道具(hull材質を流用)は動くのでここには入れない。
  // マスト/索具(高所)まで入れると水上に「見えない柱」ができるため船体部(y<30)だけ拾う。
  const lowOnly = new Set<any>();
  scene.traverse((o: any) => {
    if (!(o.isMesh && o.geometry && o.geometry.attributes && o.geometry.attributes.position)) return;
    const mat = Array.isArray(o.material) ? (o.material[0] && o.material[0].name) : (o.material && o.material.name);
    if (mat === 'sw02mat' && o.parent === scene) { meshes.push(o); lowOnly.add(o); }
  });
  if (!meshes.length) return null;

  // world 三角形を集めつつ xz bbox を取得
  const tris: number[] = []; // [ax,ay,az, bx,by,bz, cx,cy,cz, ...]
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const m of meshes) {
    m.updateWorldMatrix(true, false);
    const e = m.matrixWorld.elements as number[];
    const pos = m.geometry.attributes.position;
    const idx = m.geometry.index;
    const wx = (x: number, y: number, z: number) => e[0] * x + e[4] * y + e[8] * z + e[12];
    const wy = (x: number, y: number, z: number) => e[1] * x + e[5] * y + e[9] * z + e[13];
    const wz = (x: number, y: number, z: number) => e[2] * x + e[6] * y + e[10] * z + e[14];
    const push = (i: number) => {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const X = wx(x, y, z), Y = wy(x, y, z), Z = wz(x, y, z);
      tris.push(X, Y, Z);
      if (X < minX) minX = X; if (X > maxX) maxX = X;
      if (Z < minZ) minZ = Z; if (Z > maxZ) maxZ = Z;
    };
    const cnt = idx ? idx.count : pos.count;
    for (let t = 0; t + 2 < cnt; t += 3) {
      push(idx ? idx.getX(t) : t);
      push(idx ? idx.getX(t + 1) : t + 1);
      push(idx ? idx.getX(t + 2) : t + 2);
      if (lowOnly.has(m)) { // 難破物は船体部だけ: 全頂点がy≥30の三角形(マスト等)は捨てる
        const L = tris.length;
        if (Math.min(tris[L - 8], tris[L - 5], tris[L - 2]) >= 30) tris.length = L - 9;
      }
    }
  }
  if (!isFinite(minX)) return null;

  const cellX = (maxX - minX) / GRID, cellZ = (maxZ - minZ) / GRID;
  const H = new Float32Array(GRID * GRID).fill(-Infinity);

  for (let i = 0; i < tris.length; i += 9) {
    const ax = tris[i], ay = tris[i + 1], az = tris[i + 2];
    const bx = tris[i + 3], by = tris[i + 4], bz = tris[i + 5];
    const cx = tris[i + 6], cy = tris[i + 7], cz = tris[i + 8];
    const tMinX = Math.min(ax, bx, cx), tMaxX = Math.max(ax, bx, cx);
    const tMinZ = Math.min(az, bz, cz), tMaxZ = Math.max(az, bz, cz);
    const ci0 = Math.max(0, Math.floor((tMinX - minX) / cellX));
    const ci1 = Math.min(GRID - 1, Math.floor((tMaxX - minX) / cellX));
    const cj0 = Math.max(0, Math.floor((tMinZ - minZ) / cellZ));
    const cj1 = Math.min(GRID - 1, Math.floor((tMaxZ - minZ) / cellZ));
    // 頂点スプラット: セル中心テストだけだとセル幅より細い岩(水面上の小岩など)が丸ごと欠落し、
    // 泳いで岩の中に入れてしまう。頂点のyを属するセルへ直接書き込んで細い地物も必ず拾う。
    // 水面上に出る頂点(y>2)はbilinear補間の支持セル(2×2)全部へ書き、岩の間の補間の谷も塞ぐ。
    for (let v = 0; v < 9; v += 3) {
      const vy = tris[i + v + 1];
      const fx = (tris[i + v] - minX) / cellX, fz = (tris[i + v + 2] - minZ) / cellZ;
      const wide = vy > 2 ? 1 : 0;
      const i0 = Math.floor(fx - 0.5 * wide), j0 = Math.floor(fz - 0.5 * wide);
      for (let cj = j0; cj <= j0 + wide; cj++) for (let ci = i0; ci <= i0 + wide; ci++) {
        if (ci < 0 || ci >= GRID || cj < 0 || cj >= GRID) continue;
        const k = cj * GRID + ci;
        if (vy > H[k]) H[k] = vy;
      }
    }
    const d = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz);
    if (Math.abs(d) < 1e-9) continue;
    for (let cj = cj0; cj <= cj1; cj++) {
      const pz = minZ + (cj + 0.5) * cellZ;
      for (let ci = ci0; ci <= ci1; ci++) {
        const px = minX + (ci + 0.5) * cellX;
        const l1 = ((bz - cz) * (px - cx) + (cx - bx) * (pz - cz)) / d;
        const l2 = ((cz - az) * (px - cx) + (ax - cx) * (pz - cz)) / d;
        const l3 = 1 - l1 - l2;
        if (l1 >= -0.002 && l2 >= -0.002 && l3 >= -0.002) {
          const y = l1 * ay + l2 * by + l3 * cy;
          const k = cj * GRID + ci;
          if (y > H[k]) H[k] = y;
        }
      }
    }
  }
  return { minX, minZ, cellX, cellZ, N: GRID, H, bounds: { minX, maxX, minZ, maxZ } };
}

// 潜水の床専用: 島棚の縁から海底(floorMin)まで連続的に下る「裾野」を外側へ膨張生成する。
// 見た目の棚は有限深度(実測: スポーン砂州の南で-115)で途切れ、その1歩先で床が一気に-240へ
// 落ちるため、棚の裏側を見上げられた(=海底貫通に見える)。裾野で床を連続化して塞ぐ。
// 【注意】クレート等の配置は見た目と一致する従来のhfを使うこと(裾野には視覚メッシュがない)。
export function buildFloorField(hf: Heightfield, floorMin: number): Heightfield {
  // 勾配・裾野長はセル寸法に適応(GRID解像度が変わっても世界座標で約31°=実測の棚勾配0.6を保つ)
  const cell = Math.min(hf.cellX, hf.cellZ);
  const DROP = Math.max(3, 0.6 * cell);            // 1セルあたりの下り
  const PAD = Math.ceil(-floorMin / DROP) + 2;     // 最浅の縁(≈0)からfloorMinまで届くセル数+余裕
  const N = hf.N + PAD * 2;
  const minX = hf.minX - PAD * hf.cellX, minZ = hf.minZ - PAD * hf.cellZ;
  const H = new Float32Array(N * N).fill(-Infinity);
  for (let j = 0; j < hf.N; j++) for (let i = 0; i < hf.N; i++) H[(j + PAD) * N + (i + PAD)] = hf.H[j * hf.N + i];
  for (let pass = 0; pass < PAD + 8; pass++) {
    const src = H.slice();                         // 前パスの値から拡張(同パス内の連鎖を防ぐ)
    let changed = false;
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const k = j * N + i;
      if (src[k] !== -Infinity) continue;
      let best = -Infinity;
      if (i > 0 && src[k - 1] > best) best = src[k - 1];
      if (i < N - 1 && src[k + 1] > best) best = src[k + 1];
      if (j > 0 && src[k - N] > best) best = src[k - N];
      if (j < N - 1 && src[k + N] > best) best = src[k + N];
      if (best === -Infinity) continue;
      H[k] = Math.max(best - DROP, floorMin);
      changed = true;
    }
    if (!changed) break;
  }
  return { minX, minZ, cellX: hf.cellX, cellZ: hf.cellZ, N, H, bounds: { minX, maxX: minX + N * hf.cellX, minZ, maxZ: minZ + N * hf.cellZ } };
}

// (x,z) の地表高さ。島の外/穴は -Infinity。双線形補間。
export function groundHeight(hf: Heightfield | null, x: number, z: number): number {
  if (!hf) return -Infinity;
  const fx = (x - hf.minX) / hf.cellX - 0.5;
  const fz = (z - hf.minZ) / hf.cellZ - 0.5;
  const i = Math.floor(fx), j = Math.floor(fz);
  if (i < 0 || j < 0 || i >= hf.N - 1 || j >= hf.N - 1) return -Infinity;
  const tx = fx - i, tz = fz - j;
  const N = hf.N, H = hf.H;
  const h00 = H[j * N + i], h10 = H[j * N + i + 1], h01 = H[(j + 1) * N + i], h11 = H[(j + 1) * N + i + 1];
  const NEG = -Infinity;
  if (h00 === NEG || h10 === NEG || h01 === NEG || h11 === NEG) {
    let mx = NEG;
    for (const v of [h00, h10, h01, h11]) if (v > mx) mx = v;
    return mx; // 一部欠損セルは有効値の最大で代替（縁の処理）
  }
  return (h00 * (1 - tx) + h10 * tx) * (1 - tz) + (h01 * (1 - tx) + h11 * tx) * tz;
}
